import { useAxios } from "../useAxios"
import type { IAxiosParams } from "../useAxios"

export interface ISharedInboxAccount {
    accountId: string
    identityId: string | null
    name: string
    owner: { _id: string; name: string } | null
    shared: boolean
    isOwner: boolean
    canManage: boolean
    /** connected | credentials | not_found | error … (live from the provider) */
    status: string
    unreadCount: number
    sync: {
        status: 'idle' | 'running' | 'error' | string
        lastSyncedAt: string | null
        backfilledAt: string | null
        lastError: string
    }
}

/**
 * The shared inbox API (/inbox/shared). The conversation calls deliberately carry the same
 * names, arguments and response shapes as useConversationsRequests, so the inbox handlers
 * can use either one without knowing which inbox they serve.
 */
export const useSharedInboxRequests = () => {
    const { sendRequest } = useAxios()

    const route = '/inbox/shared/conversations'

    /**
     * A write that resolves only when the server accepted it, and rejects with the server's
     * own message otherwise.
     *
     * useAxios swallows failures (it resolves with undefined or the error body), so a refused
     * star or pin used to look done — the handler showed "starred", and the next refresh of
     * the list quietly put it back. Rejecting lets every handler's catch run instead.
     * The slug is unique per call: a second click must not cancel the first write.
     */
    const write = async (params: Omit<IAxiosParams, 'slug'> & { slug: string }) => {
        const response: any = await sendRequest({ ...params, slug: `${params.slug}:${Date.now()}`, displayToast: false, all: true })
        if (!response?.success) {
            throw new Error(response?.message || 'The server did not accept this change. Please try again.')
        }
        return response.data
    }

    // ── Same surface as useConversationsRequests ──

    /** `background`: a periodic refresh — its own slug, so it never cancels (or is cancelled by) a load the user started. */
    const paginateConversations = (payload: { page: number; filters?: any; isSales?: boolean; background?: boolean }) => {
        const scope = payload.isSales === true ? 'sn' : payload.isSales === false ? 'in' : 'all'
        const filterScope = payload.filters ? JSON.stringify(payload.filters) : 'none'
        return sendRequest({
            slug: `sharedPaginateConversations:${payload.background ? 'bg:' : ''}${payload.page}:${scope}:${filterScope}`,
            url: `${route}/paginate`,
            method: 'GET',
            params: {
                page: payload.page,
                ...(payload.isSales !== undefined ? { isSales: payload.isSales } : {}),
                ...(payload.filters && { filters: JSON.stringify(payload.filters) })
            },
            displayToast: false
        })
    }

    const updateConversations = (payload: any) => write({
        slug: `sharedUpdateConversations`,
        url: `${route}/bulk/actions/update`,
        method: 'PATCH',
        payload,
    })

    const starConversation = (conversationId: string, isStarred: boolean) => starConversations([conversationId], isStarred)

    const starConversations = (conversationIds: string[], isStarred: boolean) => write({
        slug: `sharedStarConversations`,
        url: `${route}/bulk/actions/star`,
        method: 'PATCH',
        payload: { conversations: conversationIds, isStarred },
    })

    const pinConversation = (conversationId: string, isPinned: boolean) => pinConversations([conversationId], isPinned)

    const pinConversations = (conversationIds: string[], isPinned: boolean) => write({
        slug: `sharedPinConversations`,
        url: `${route}/bulk/actions/pin`,
        method: 'PATCH',
        payload: { conversations: conversationIds, isPinned },
    })

    const snoozeConversations = (conversationIds: string[], snoozedUntil: number | null) => write({
        slug: `sharedSnoozeConversations`,
        url: `${route}/bulk/actions/snooze`,
        method: 'PATCH',
        payload: { conversations: conversationIds, snoozedUntil },
    })

    const applyTagsToConversations = (payload: { tags: any[]; connectionIds: string[]; conversationIds: string[]; shouldRefetchTags?: boolean }) => write({
        slug: `sharedApplyTagsToConversations`,
        url: `${route}/bulk/actions/tags/apply`,
        method: 'PATCH',
        payload,
    })

    const removeTagsFromConversations = (payload: { tags: string[]; connectionIds: string[]; conversationIds: string[] }) => write({
        slug: `sharedRemoveTagsFromConversations`,
        url: `${route}/bulk/actions/tags/remove`,
        method: 'PATCH',
        payload,
    })

    const applyRemoveTagsFromConversations = (payload: { tagsToAdd: any[]; tagsToRemove: string[]; connectionIds: string[]; conversationIds: string[] }) => write({
        slug: `sharedApplyRemoveTagsFromConversations`,
        url: `${route}/tags/apply-remove`,
        method: 'POST',
        payload,
    })

    const getConversationsTagStatus = (conversationIds: string[]) => {
        return sendRequest({
            slug: `sharedGetConversationsTagStatus`,
            url: `${route}/tag-status`,
            method: 'GET',
            params: { conversationIds: conversationIds.join(',') },
            displayToast: false
        })
    }

    /** A thread lives on the LinkedIn account it belongs to; nobody but its owner deletes it there. */
    const deleteConversation = (_conversationId: string) =>
        Promise.reject(new Error('Conversations cannot be deleted from the shared inbox.'))

    // ── Shared inbox only ──

    const getAccounts = () => {
        return sendRequest({
            slug: `sharedInboxAccounts`,
            url: `/inbox/shared/accounts`,
            method: 'GET',
            displayToast: false
        })
    }

    const updateAccountSharing = (accountId: string, shared: boolean) => {
        return sendRequest({
            slug: `sharedInboxAccountSharing:${accountId}`,
            url: `/inbox/shared/accounts/${encodeURIComponent(accountId)}`,
            method: 'PATCH',
            payload: { shared },
            displayToast: false,
            displayErrorToast: true,
        })
    }

    const syncAccount = (accountId: string) => {
        return sendRequest({
            slug: `sharedInboxAccountSync:${accountId}`,
            url: `/inbox/shared/accounts/${encodeURIComponent(accountId)}/sync`,
            method: 'POST',
            displayToast: false,
            displayErrorToast: true,
        })
    }

    /** One page of a thread, oldest → newest; `cursor` (from the previous page) walks back in time. */
    const getMessages = (conversationId: string, cursor?: string | null) => {
        return sendRequest({
            slug: `sharedInboxMessages:${conversationId}:${cursor || 'first'}`,
            url: `${route}/${encodeURIComponent(conversationId)}/messages`,
            method: 'GET',
            params: { ...(cursor ? { cursor } : {}), limit: 20 },
            displayToast: false
        })
    }

    /** Resolves to the whole envelope ({ success, message, data }) so a refusal's reason reaches the user. */
    const sendMessage = (conversationId: string, payload: { text: string; attachments?: any[] }) => {
        return sendRequest({
            slug: `sharedInboxSend:${conversationId}:${Date.now()}`,
            url: `${route}/${encodeURIComponent(conversationId)}/messages`,
            method: 'POST',
            payload,
            displayToast: false,
            all: true,
        })
    }

    return {
        paginateConversations,
        updateConversations,
        starConversation,
        starConversations,
        pinConversation,
        pinConversations,
        snoozeConversations,
        applyTagsToConversations,
        removeTagsFromConversations,
        applyRemoveTagsFromConversations,
        getConversationsTagStatus,
        deleteConversation,
        getAccounts,
        updateAccountSharing,
        syncAccount,
        getMessages,
        sendMessage,
    }
}
